import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Eye, Pen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Textarea } from 'components/ui/textarea';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import { cn } from 'lib/utils';
import { MarkdownEditorCore } from './markdown-editor-core';
import { MarkdownEditorToolbar } from './markdown-editor-toolbar';

type MarkdownEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  contentClassName?: string;
  error?: string;
  isInvalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showToolbar?: boolean;
  showModeToggle?: boolean;
  defaultMode?: 'edit' | 'preview';
  minLength?: number;
  maxLength?: number;
  showCharacterCount?: boolean;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  onModeChange?: (mode: 'edit' | 'preview') => void;
  toolbarItems?: (keyof EditorCommands)[];
  validate?: (value: string) => string | undefined;
};

export type MarkdownEditorRef = {
  focus: () => void;
  blur: () => void;
  getValue: () => string;
  setValue: (value: string) => void;
  getMode: () => 'edit' | 'preview';
  setMode: (mode: 'edit' | 'preview') => void;
  validate: () => string | undefined;
};

export type EditorCommands = {
  h1: () => void;
  h2: () => void;
  h3: () => void;
  bold: () => void;
  italic: () => void;
  unordered: () => void;
  ordered: () => void;
  task: () => void;
  quote: () => void;
  code: () => void;
  link: () => void;
};

export const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  (
    {
      value = '',
      onChange,
      className = '',
      contentClassName = '',
      error,
      isInvalid = false,
      placeholder,
      disabled = false,
      showToolbar = true,
      showModeToggle = true,
      defaultMode = 'edit',
      minLength,
      maxLength,
      showCharacterCount = false,
      onBlur,
      onFocus,
      onModeChange,
      toolbarItems = [
        'h1',
        'h2',
        'h3',
        'bold',
        'italic',
        'unordered',
        'ordered',
        'task',
        'quote',
        'code',
        'link',
      ],
      validate,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<'edit' | 'preview'>(defaultMode);
    const [internalError, setInternalError] = useState('');
    const [content, setContent] = useState(value);
    const resolvedPlaceholder =
      placeholder ?? t('ENTER_YOUR_CONTENT', { defaultValue: 'Enter your content...' });

    const coreRef = useRef<MarkdownEditorCore>(new MarkdownEditorCore(value));
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      coreRef.current.setContent(value);
      setContent(value);
    }, [value]);

    const performValidation = (val: string): string => {
      if (validate) {
        const validationResult = validate(val);
        if (validationResult) return validationResult;
      }
      if (minLength && val.length < minLength) {
        return t('MINIMUM_CHARACTERS_REQUIRED', {
          min: minLength,
          defaultValue: `Minimum ${minLength} characters required`,
        });
      }
      if (maxLength && val.length > maxLength) {
        return t('MAXIMUM_CHARACTERS_ALLOWED', {
          max: maxLength,
          defaultValue: `Maximum ${maxLength} characters allowed`,
        });
      }
      return '';
    };

    const handleModeChange = (newMode: string) => {
      const modeValue = newMode as 'edit' | 'preview';
      setMode(modeValue);
      onModeChange?.(modeValue);
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      const val = event.target.value;
      coreRef.current.setContent(val).setCursor(event.target.selectionStart, event.target.selectionEnd);
      setContent(val);
      onChange?.(val);
      if (internalError) setInternalError('');
    };

    const handleBlur = () => {
      const validationError = performValidation(content);
      setInternalError(validationError);
      onBlur?.(content);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        execCommand(coreRef.current.handleEnter);
      }
    };

    const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      const element = event.currentTarget;
      coreRef.current.setCursor(element.selectionStart, element.selectionEnd);
    };

    const execCommand = (
      commandFn: () => { content: string; cursor: { start: number; end: number } },
    ) => {
      if (disabled) return;

      const { content: nextContent, cursor } = commandFn();
      if (maxLength && nextContent.length > maxLength) return;

      setContent(nextContent);
      onChange?.(nextContent);
      const element = textareaRef.current;
      if (!element) return;
      const { start, end } = cursor;
      requestAnimationFrame(() => {
        element.selectionStart = start;
        element.selectionEnd = end;
        element.focus();
      });
    };

    const commands = {
      h1: () => execCommand(coreRef.current.h1),
      h2: () => execCommand(coreRef.current.h2),
      h3: () => execCommand(coreRef.current.h3),
      bold: () => execCommand(coreRef.current.bold),
      italic: () => execCommand(coreRef.current.italic),
      unordered: () => execCommand(coreRef.current.unordered),
      ordered: () => execCommand(coreRef.current.ordered),
      task: () => execCommand(coreRef.current.task),
      quote: () => execCommand(coreRef.current.quote),
      code: () => execCommand(coreRef.current.code),
      link: () => execCommand(coreRef.current.link),
    };

    const filteredCommands = Object.fromEntries(
      Object.entries(commands).filter(([key]) => toolbarItems.includes(key as keyof EditorCommands)),
    ) as Partial<EditorCommands>;

    const displayError = error || internalError;
    const hasError = isInvalid || !!displayError;
    const characterCount = content.length;

    React.useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      blur: () => textareaRef.current?.blur(),
      getValue: () => content,
      setValue: (nextValue: string) => {
        coreRef.current.setContent(nextValue);
        setContent(nextValue);
        onChange?.(nextValue);
      },
      getMode: () => mode,
      setMode: (nextMode: 'edit' | 'preview') => setMode(nextMode),
      validate: () => performValidation(content) || undefined,
    }));

    const hasFixedHeight = /\bh(?:-\[|-.)/.test(className);

    return (
      <div className="flex min-h-0 w-full flex-col gap-3">
        {(showToolbar || showModeToggle) && (
          <div className="sticky top-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-2 bg-background px-5 py-4 md:px-0">
            {showToolbar && (
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <MarkdownEditorToolbar
                  commands={filteredCommands as EditorCommands}
                  disabled={disabled || mode === 'preview'}
                />
              </div>
            )}
            {showModeToggle && (
              <Tabs value={mode} onValueChange={handleModeChange} className="shrink-0">
                <TabsList className="h-9 rounded-md border bg-muted p-0.5">
                  <TabsTrigger
                    value="edit"
                    className="h-8 min-w-10 rounded-sm px-3 text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                    aria-label={t('EDIT_MODE', { defaultValue: 'Edit mode' })}
                  >
                    <Pen className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger
                    value="preview"
                    className="h-8 min-w-10 rounded-sm px-3 text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                    aria-label={t('PREVIEW_MODE', { defaultValue: 'Preview mode' })}
                  >
                    <Eye className="h-4 w-4" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
        )}

        <div
          className={cn(
            'mx-5 flex min-h-0 flex-col rounded-xl border bg-background transition-colors md:mx-0',
            hasFixedHeight ? 'overflow-hidden' : 'overflow-clip',
            hasError ? 'border-destructive' : 'border-border',
            !hasFixedHeight && 'min-h-64',
            className,
          )}
        >
          {mode === 'edit' ? (
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={() => onFocus?.(content)}
              onKeyDown={handleKeyDown}
              onSelect={handleSelect}
              placeholder={resolvedPlaceholder}
              disabled={disabled}
              maxLength={maxLength}
              rows={16}
              className={cn(
                'resize-none overflow-y-auto border-0 px-4 py-3 shadow-none focus-visible:ring-0',
              )}
            />
          ) : (
            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain p-4 [overflow-wrap:anywhere]',
                !contentClassName?.includes('max-h-') && 'max-h-[min(24rem,50vh)]',
                contentClassName,
              )}
            >
              {content.trim() ? (
                <MarkdownRenderer content={content} className="max-w-none [overflow-wrap:anywhere]" />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('NOTHING_TO_PREVIEW_YET', { defaultValue: 'Nothing to preview yet.' })}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-start justify-between gap-3 mx-5 md:mx-0">
          <div className="min-h-5 text-xs text-destructive">{displayError}</div>
          {showCharacterCount && (
            <div className="text-xs text-muted-foreground">
              {characterCount}
              {maxLength ? `/${maxLength}` : ''}
            </div>
          )}
        </div>
      </div>
    );
  },
);

MarkdownEditor.displayName = 'MarkdownEditor';
